import { cn } from "../lib/cn";

export type LedgerAlign = "left" | "right";

const alignClasses: Record<LedgerAlign, string> = {
  left: "text-left",
  right: "text-right",
};

export interface LedgerTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /** Merged onto the paper wrapper, not the inner `<table>`. */
  className?: string;
  ref?: React.Ref<HTMLTableElement>;
}

/** Green-bar ledger paper for audit trails. */
export function LedgerTable({
  children,
  className,
  ref,
  ...rest
}: LedgerTableProps) {
  return (
    <div
      className={cn(
        "overflow-x-auto bg-card border border-rule rounded-paper",
        className,
      )}
    >
      <table ref={ref} className="w-full border-collapse" {...rest}>
        {children}
      </table>
    </div>
  );
}

export interface LedgerHeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  ref?: React.Ref<HTMLTableSectionElement>;
}

export function LedgerHead({
  children,
  className,
  ref,
  ...rest
}: LedgerHeadProps) {
  return (
    <thead ref={ref} className={cn(className)} {...rest}>
      {children}
    </thead>
  );
}

export interface LedgerHeadCellProps extends Omit<
  React.ThHTMLAttributes<HTMLTableCellElement>,
  "align"
> {
  align?: LedgerAlign;
  ref?: React.Ref<HTMLTableCellElement>;
}

export function LedgerHeadCell({
  align = "left",
  children,
  className,
  ref,
  ...rest
}: LedgerHeadCellProps) {
  return (
    <th
      ref={ref}
      scope="col"
      className={cn(
        "text-annotation-caps text-annotation px-3 py-2 border-b border-rule",
        alignClasses[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}

export interface LedgerBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  ref?: React.Ref<HTMLTableSectionElement>;
}

export function LedgerBody({
  children,
  className,
  ref,
  ...rest
}: LedgerBodyProps) {
  return (
    <tbody ref={ref} className={cn(className)} {...rest}>
      {children}
    </tbody>
  );
}

export interface LedgerRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  ref?: React.Ref<HTMLTableRowElement>;
}

export function LedgerRow({
  children,
  className,
  ref,
  ...rest
}: LedgerRowProps) {
  return (
    <tr
      ref={ref}
      className={cn(
        "even:bg-[color-mix(in_srgb,var(--color-wash-working)_16%,transparent)]",
        "border-b border-rule/50",
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

export interface LedgerCellProps extends Omit<
  React.TdHTMLAttributes<HTMLTableCellElement>,
  "align"
> {
  mono?: boolean;
  align?: LedgerAlign;
  ref?: React.Ref<HTMLTableCellElement>;
}

export function LedgerCell({
  mono = false,
  align = "left",
  children,
  className,
  ref,
  ...rest
}: LedgerCellProps) {
  return (
    <td
      ref={ref}
      className={cn(
        "px-3 py-2 text-sm text-ink-body",
        mono && "font-mono text-[0.8125rem]",
        alignClasses[align],
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}
